import { computed, ref } from "vue";
import {
  Address,
  Barcode,
  Customer,
  Item,
  ItemCategory,
  ItemPackingPrice,
  PaymentType,
  RestaurantTables,
  SalesDocument,
  SalesInvoice,
} from "../types/models";
import MessageBox from "../services/MessageBox";
import { defineStore } from "pinia";
import { tokenManager } from "../api/utils/tokenManager";
import { roundToDecimalPlace } from "../utils/CommonFunctions";

export const posStore = defineStore("pos", () => {
  const dbName = "FaseyhaPOSProductDBs";
  const storeName = "FaseyhaPOSProductList";
  const products = ref<Item[]>([]);

  //Product Sync
  const openDB = (): Promise<IDBDatabase> => {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(dbName, 1);

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains(storeName)) {
          db.createObjectStore(storeName, { keyPath: "code" });
        }
      };

      request.onsuccess = () => {
        resolve(request.result);
      };

      request.onerror = () => {
        reject(request.error);
      };
    });
  };

  const saveProducts = async (productList: Item[]) => {
    const db = await openDB();
    const transaction = db.transaction(storeName, "readwrite");
    const store = transaction.objectStore(storeName);
    store.clear();
    productList.forEach((product) => {
      store.put(product);
    });
    return new Promise<void>((resolve, reject) => {
      transaction.oncomplete = () => {
        products.value = productList;
        resolve();
      };
      transaction.onerror = () => {
        reject(transaction.error);
      };
    });
  };
  const getAllProducts = async (): Promise<Item[]> => {
    const db = await openDB();
    const transaction = db.transaction(storeName, "readonly");
    const store = transaction.objectStore(storeName);

    let searchResults: any[] = []; // Initialize the searchResults array

    let cursorRequest = store.openCursor();

    return new Promise((resolve, reject) => {
      cursorRequest.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest).result;
        if (cursor) {
          searchResults.push(cursor.value); // Push the cursor value to searchResults
          cursor.continue(); // Continue to the next record
        } else {
          resolve(searchResults); // Resolve the promise with the searchResults array
        }
      };

      cursorRequest.onerror = () => {
        reject(cursorRequest.error); // Reject the promise with the error
      };
    });
  };

  const payment_types = ref<PaymentType[]>([]);

  const selectedTable = ref<RestaurantTables | null>(null);

  const totalProductsCount = ref(0);
  const totalPages = ref(1);
  const searchProducts = async (
    query: string,
    page: number = 1,
    pageSize: number = 30
  ): Promise<Item[]> => {
    const db = await openDB();
    const transaction = db.transaction(storeName, "readonly");
    const store = transaction.objectStore(storeName);
    const searchResults: Item[] = [];

    const request = store.openCursor();

    return new Promise((resolve, reject) => {
      request.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest).result;
        if (cursor) {
          const product = cursor.value;

          if (query) {
            const lowerQuery = query.toLowerCase();
            const hasSpace = lowerQuery.includes(" ");
            const words = hasSpace ? lowerQuery.split(" ") : [lowerQuery];

            const isMatch = hasSpace
              ? words.every((word) => product.name.toLowerCase().includes(word))
              : product.code.toLowerCase() === lowerQuery ||
                product.name.toLowerCase().includes(lowerQuery) ||
                (product.packing_prices &&
                  product.packing_prices.some(
                    (packingPrice: ItemPackingPrice) =>
                      (packingPrice.code ?? "").toLowerCase() === lowerQuery
                  )) ||
                (product.barcodes &&
                  product.barcodes.some(
                    (barcode: Barcode) =>
                      barcode.code.toLowerCase() === lowerQuery
                  ));

            if (isMatch) {
              searchResults.push(product);
            }
          } else {
            searchResults.push(product);
          }
          cursor.continue();
        } else {
          const start = (page - 1) * pageSize;
          const end = page * pageSize;
          totalProductsCount.value = searchResults.length;
          totalPages.value = Math.ceil(searchResults.length / pageSize);
          products.value = searchResults.slice(start, end);
          resolve(products.value);
        }
      };

      request.onerror = () => {
        reject(request.error);
      };
    });
  };

  //End Product Sync
  const posInvoices = ref<SalesInvoice[]>([]);
  const activeInvoiceIndex = ref(0);
  const selectedPosInvoice = ref<SalesInvoice | null>(null);

  const categories = ref(<ItemCategory[]>[]);

  const addNewPosInvoice = () => {
    let count = posInvoices.value.length + 1;
    posInvoices.value.push({
      id: count,
      title: "POS " + count,
      invoice: {
        id: "",
        sales_quotations_id: "",
        sales_orders_id: "",
        sales_goods_deliverys_id: "",
        sales_channels_id: "",
        outlets_id: "",
        date_time: "",
        customers_id: "",
        delivery_date: "",
        delivery_address_id: "",
        credit_terms_id: "",
        sales_reps_id: "",
        prefix: "",
        suffix: "",
        ref_no: "",
        shift_end_reports_id: "",
        price_lists_id: "",
        currencys_id: "",
        warehouses_id: "",
        outlet_pos_stations_id: "",
        customer_reference: "",
        remarks: "",
        attachment: "",
        uid: "",
        status_id: "",
        sub_total: 0,
        discount_a: 0,
        value_before_tax: 0,
        tax_amount: 0,
        net_total: 0,
        due_date: "",
        exchange_rate: 0,
        to_deliver: false,
        users_id: 0,
        items: [],
        payments: [],
      } as SalesDocument,
    });
    switchTab(posInvoices.value.length - 1);
  };

  const switchTab = (index: number) => {
    activeInvoiceIndex.value = index;
    selectedPosInvoice.value = posInvoices.value[index];
  };

  const newInvoice = () => {
    selectedPosInvoice.value = {
      id: 1,
      title: "Mobile Sales " + 1,
      invoice: {
        id: "",
        sales_quotations_id: "",
        sales_orders_id: "",
        sales_goods_deliverys_id: "",
        sales_channels_id: "",
        outlets_id: "",
        date_time: "",
        customers_id: "",
        delivery_date: "",
        delivery_address_id: "",
        credit_terms_id: "",
        sales_reps_id: "",
        prefix: "",
        suffix: "",
        ref_no: "",
        shift_end_reports_id: "",
        price_lists_id: "",
        currencys_id: "",
        warehouses_id: "",
        outlet_pos_stations_id: "",
        customer_reference: "",
        remarks: "",
        attachment: "",
        uid: "",
        status_id: "",
        sub_total: 0,
        discount_a: 0,
        value_before_tax: 0,
        tax_amount: 0,
        net_total: 0,
        due_date: "",
        exchange_rate: 0,
        to_deliver: false,
        users_id: 0,
        items: [],
        payments: [],
      } as SalesDocument,
    };
    customerSearch.value = "";
  };

  const closeTab = (index: number) => {
    MessageBox.confirm({
      title: "Close POS TAB",
      message:
        "There are items in the cart. Are you sure you want to close this tab? All items will be lost.",
      confirmText: "CLOSE TAB",
      cancelText: "KEEP TAB",
    }).then((confirmed: any) => {
      if (confirmed) {
        posInvoices.value.splice(index, 1);
      }
    });
  };

  const updateLineTotal = (item: Item) => {
    item.inventory_qty = (item.quantity || 1) * (item.sales_uom_qty || 1);
    item.sub_total = (item.quantity || 1) * (item.price || 0);
    if (item.ignore_discount) {
      item.discount_p = 0;
    } else {
      item.discount_a = roundToDecimalPlace(
        (item.sub_total * item.discount_p || 0) / 100
      );
    }
    item.value_before_tax = item.sub_total - item.discount_a;
    item.tax_amount = roundToDecimalPlace(
      ((item.sub_total - item.discount_a) * (item.tax_rate || 0)) / 100
    );
    item.net_total = roundToDecimalPlace(
      item.sub_total - item.discount_a + item.tax_amount
    );

    item.value_before_tax = item.net_total - item.tax_amount;

    /*let rounding = tokenManager.getSetting('sales_documents_rounding')
    if (rounding === 'Round Up to 0 decimals' && item.net_total > 0) {
      item.net_total = Math.ceil(item.net_total)
      item.price = item.net_total/item.quantity
    } else if (rounding == 'Round Down to 0 decimals' && item.net_total > 0) {
      item.net_total = Math.floor(item.net_total)
      item.price = item.net_total/item.quantity
    } else if (rounding == 'Round to 0 decimals' && item.net_total > 0) {
      item.net_total = Math.round(item.net_total)
      item.price = item.net_total/item.quantity
    }*/
  };

  const addProductToInvoice = async (product: Item, quantity = 1) => {
    if (!selectedPosInvoice.value) return;
    console.log("Adding product to invoice", product, quantity);
    let item = selectedPosInvoice.value.invoice.items.find(
      (i) => i.code === product.code
    );
    if (item) {
      if (product.flexi_price) {
        MessageBox.show(
          "The product requires a flexible price and the item is already in the cart. <br /> Please update the quantity or remove the item first."
        );
        return;
      }
      item.quantity += quantity;
      await updateLineTotal(item);
    } else {
      if (product.flexi_price) {
        const res = await MessageBox.confirm({
          title: "Flexible Price",
          message: "Enter the Flexible Price for the product " + product.name,
          confirmText: "UPDATE",
          cancelText: "CANCEL",
          requireInput: true,
          inputCaption: "Flexible Price",
          showInput: true,
          inputType: "number",
        }).then((confirmed: any) => {
          console.log("Flexible price confirmed1", confirmed);
          if (confirmed.confirmed == true) {
            console.log("Flexible price cancelled", confirmed);
            product.flexi_price = false; // Reset flexi_price if cancelled
            product.price = confirmed.input || product.price;
            product.selling_price = confirmed.input || product.selling_price;
          } else {
            return;
          }
        });
        console.log("Flexible price set to", product);
        console.log("res", res);
      }
      let item = {
        id: "",
        photo: product.photo,
        code: product.code || product.item_code,
        name: product.name || product.item || product.item_name,
        item: product.name || product.item || product.item_name,
        item_name: product.name || product.item || product.item_name,
        ignore_discount: product.ignore_discount || false,
        item_type: product.item_type,
        sales_name: product.sales_name,
        item_types_id: product.item_types_id,
        tracked_by: product.tracked_by,
        purchase_uom: product.purchase_uom,
        purchase_uom_qty: product.purchase_uom_qty,
        sales_uom: product.sales_uom,
        sales_uom_qty: product.sales_uom_qty,
        flexi_price: product.flexi_price,
        default_sales_uom_qty: product.sales_uom_qty ?? 1,
        default_sales_uom_id: product.sales_uom_id ?? "", // Ensure not undefined
        is_package: product.is_package,
        use_package_price: product.use_package_price,
        is_expiry: product.is_expiry,
        tax_details_id: product.tax_details_id,
        inventory_uom_id: product.inventory_uom_id,
        inventory_uom_qty: product.inventory_uom_qty,
        purchase_uom_id: product.purchase_uom_id,
        sales_uom_id: product.sales_uom_id,
        default_purchase_uom_qty: product.sales_uom_qty,
        has_variants: product.has_variants,
        variants: product.variants,
        tax: product.tax,
        tax_rate: product.tax_rate,
        uid: product.uid,
        warehouses_id: product.warehouses_id,
        price_lists_id: product.price_lists_id,
        items_id: product.items_id,
        item_variants_id: product.item_variants_id,
        unit_of_measures_id: product.unit_of_measures_id,
        unit_of_measure: product.unit_of_measure,
        on_hand: product.on_hand,
        quantity: quantity,
        uom_qty: product.uom_qty,
        inventory_qty: product.inventory_qty,
        cost: product.cost,
        original_price: product.selling_price ?? product.price,
        selling_price: product.selling_price ?? product.price,
        price: product.selling_price ?? product.price,
        sub_total: product.selling_price ?? product.price,
        discount_p: discount.value ?? 0,
        discount_a: 0,
        value_before_tax: (product.selling_price ?? product.price) * quantity,
        tax_amount: 0,
        net_total: (product.selling_price ?? product.price) * quantity,
        remarks: "",
        batches: [],
        serials: [],
        quantity_prices: product.quantity_prices,
        packing_prices: [],
        shelfs: [],
        variant_list: [],
        variant_name: product.variant_name,
      };
      updateLineTotal(item);
      selectedPosInvoice.value.invoice.items.push(item);
    }
  };

  const updateQuantity = (item: Item, quantity: number) => {
    if (quantity == 0) {
      MessageBox.confirm({
        title: "Remvove",
        message: "Do you want to remove the item from Cart?",
        confirmText: "YES",
        cancelText: "NO",
      }).then((confirmed: any) => {
        if (confirmed) {
          if (selectedPosInvoice.value) {
            let index = selectedPosInvoice.value.invoice.items.findIndex(
              (i) => i.code === item.code
            );
            if (index >= 0) {
              selectedPosInvoice.value.invoice.items.splice(index, 1);
            }
          }
        }
      });
    } else {
      item.quantity = quantity;
      updateLineTotal(item);
    }
  };

  const removeItem = (item: Item) => {
    if (selectedPosInvoice.value) {
      let index = selectedPosInvoice.value.invoice.items.findIndex(
        (i) => i.code === item.code
      );
      if (index >= 0) {
        selectedPosInvoice.value.invoice.items.splice(index, 1);
      }
    }
  };

  const sub_total = computed(() => {
    return selectedPosInvoice.value?.invoice.items.reduce(
      (sum, item) => sum + item.quantity * item.selling_price,
      0
    );
  });

  const value_before_tax = computed(() => {
    return selectedPosInvoice.value?.invoice.items.reduce(
      (sum, item) => sum + (item.value_before_tax ?? 0),
      0
    );
  });

  const tax_amount = computed(() => {
    return selectedPosInvoice.value?.invoice.items.reduce(
      (sum, item) => sum + (item.tax_amount ?? 0),
      0
    );
  });

  const net_total = computed(() => {
    return roundToDecimalPlace(
      selectedPosInvoice.value?.invoice.items.reduce(
        (sum, item) => sum + (item.net_total ?? 0),
        0
      ) ?? 0
    );
  });

  const discount_amount = computed(() => {
    return selectedPosInvoice.value?.invoice.items.reduce(
      (sum, item) => sum + (item.discount_a ?? 0),
      0
    );
  });

  const service_charge = computed(() => {
    return 0
  });


  const discount = ref(0);

  const applyDiscount = (percent: number) => {
    const max_discount = tokenManager.getPosStation()?.outlet?.max_discount;
    if (!max_discount || percent > max_discount) {
      percent = max_discount ?? 0;
      discount.value = percent;
      MessageBox.show(
        "You cannot apply discount more than " + max_discount + "%"
      );
      //return;
    }

    selectedPosInvoice.value?.invoice.items.forEach((item) => {
      item.discount_p = percent;
      updateLineTotal(item);
    });
  };

  const setQuotation = (data: any) => {
    if (selectedPosInvoice.value) {
      setCustomer(data.customer);
      selectedPosInvoice.value.invoice.sales_quotations_id = data.id;
      data.items.forEach((item: Item) => {
        addProductToInvoice(item, item.quantity);
      });
    }
  };

  const customerSearch = ref("");

  const setCustomer = (customer: Customer) => {
    if (selectedPosInvoice.value && customer) {
      selectedPosInvoice.value.invoice.customers_id = customer.id;
      selectedPosInvoice.value.invoice.customer = customer;
      selectedPosInvoice.value.invoice.customer.credit_limit = Number(
        customer.credit_limit ?? 0
      );
      selectedPosInvoice.value.invoice.customer.available_credit =
        Number(customer.credit_limit ?? 0) > 0
          ? Number(customer.credit_limit ?? 0) -
            Number(customer.outstanding ?? 0)
          : 0;
      if (customer.addresses && customer.addresses.length > 0) {
        selectedPosInvoice.value.invoice.delivery_address_id =
          customer.addresses[0].id;
        selectedPosInvoice.value.invoice.customer.selectedAddress =
          customer.addresses[0];
      }
    }
  };

  const setCustomerAddress = (address: Address) => {
    if (selectedPosInvoice.value) {
      selectedPosInvoice.value.invoice.delivery_address_id = address.id;
      if (selectedPosInvoice.value.invoice.customer) {
        selectedPosInvoice.value.invoice.customer.selectedAddress = address;
      }
    }
  };

  const clearCustomer = () => {
    if (selectedPosInvoice.value) {
      selectedPosInvoice.value.invoice.customers_id = "";
      selectedPosInvoice.value.invoice.customer = {} as Customer;
    }
  };

  const voidInvoice = () => {
    customerSearch.value = "";
    discount.value = 0;
    if (selectedPosInvoice.value) {
      selectedPosInvoice.value.invoice = {
        id: "",
        sales_quotations_id: "",
        sales_orders_id: "",
        sales_goods_deliverys_id: "",
        sales_channels_id: "",
        outlets_id: "",
        date_time: "",
        customers_id: "",
        delivery_date: "",
        delivery_address_id: "",
        credit_terms_id: "",
        sales_reps_id: "",
        prefix: "",
        suffix: "",
        ref_no: "",
        shift_end_reports_id: "",
        price_lists_id: "",
        currencys_id: "",
        warehouses_id: "",
        outlet_pos_stations_id: "",
        customer_reference: "",
        remarks: "",
        attachment: "",
        uid: "",
        status_id: "",
        sub_total: 0,
        discount_a: 0,
        value_before_tax: 0,
        tax_amount: 0,
        net_total: 0,
        due_date: "",
        exchange_rate: 0,
        to_deliver: false,
        users_id: 0,
        items: [],
        payments: [],
      } as SalesDocument;
    }
  };

  const handlePrint = () => {
    console.log("Printing");
  };

  const handleUpdate = () => {
    console.log("handleUpdate");
  };

  const showCheckoutModal = ref(false);

  return {
    posInvoices,
    activeInvoiceIndex,
    selectedPosInvoice,
    addNewPosInvoice,
    switchTab,
    closeTab,
    addProductToInvoice,
    updateQuantity,
    removeItem,
    applyDiscount,
    updateLineTotal,
    setCustomer,
    setQuotation,
    setCustomerAddress,
    clearCustomer,
    handlePrint,
    handleUpdate,
    voidInvoice,
    saveProducts,
    getAllProducts,
    searchProducts,
    totalProductsCount,
    customerSearch,
    discount,
    totalPages,
    sub_total,
    tax_amount,
    net_total,
    discount_amount,
    value_before_tax,
    service_charge,
    categories,
    showCheckoutModal,
    newInvoice,
    payment_types,
    selectedTable,
  };
});
